import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getImageUrl } from '../utils/imageUtils';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocketChat } from '../contexts/WebSocketContext';
import { toast } from 'sonner';
import { 
  X, Minimize2, Maximize2, Send, Smile, User, Wifi, WifiOff
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { OnlineIndicator } from './OnlineIndicator';
import EmojiPicker from 'emoji-picker-react';

export const ChatPopup = ({ 
  partner, 
  profile, 
  isOpen, 
  onClose, 
  onMinimize, 
  isMinimized,
  position = 0 // Position from right for multiple chat windows
}) => {
  const { user, api } = useAuth();
  const { isConnected, sendMessage: wsSendMessage, subscribeToMessages } = useWebSocketChat();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const messagesEndRef = useRef(null);
  const emojiRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch initial messages and subscribe to WebSocket
  useEffect(() => {
    if (isOpen && partner?.id && !isMinimized) {
      fetchMessages();
      
      // Subscribe to WebSocket messages for this partner
      const unsubscribe = subscribeToMessages(partner.id, (data) => {
        // Add new message to the list, marking own messages
        const messageData = { ...data };
        // 'message_sent' type = message we sent ourselves via WebSocket
        if (data.type === 'message_sent') {
          messageData._isOwn = true;
        }
        setMessages(prev => {
          // Check if message already exists (prevent duplicates)
          if (prev.some(m => m.id === messageData.id)) {
            return prev;
          }
          return [...prev, messageData];
        });
      });

      return () => {
        unsubscribe();
      };
    }
  }, [isOpen, partner?.id, isMinimized, subscribeToMessages]);

  // Fallback polling only if WebSocket is not connected
  useEffect(() => {
    if (isOpen && partner?.id && !isMinimized && !isConnected) {
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [isOpen, partner?.id, isMinimized, isConnected]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiRef.current && !emojiRef.current.contains(event.target)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchMessages = async () => {
    try {
      // Fetch from both chat_messages (WebSocket) and messages (HTTP) collections
      let chatMessages = [];
      let httpMessages = [];
      
      try {
        const chatRes = await api.get(`/chat/history/${partner.id}`);
        chatMessages = chatRes.data || [];
      } catch {}
      
      try {
        const httpRes = await api.get(`/messages/${partner.id}`);
        httpMessages = httpRes.data || [];
      } catch {}
      
      // Merge and deduplicate by id, sort by date
      const allMessages = [...chatMessages, ...httpMessages];
      const uniqueMap = new Map();
      allMessages.forEach(m => {
        if (!uniqueMap.has(m.id)) {
          uniqueMap.set(m.id, m);
        }
      });
      const merged = Array.from(uniqueMap.values()).sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );
      
      setMessages(merged);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    const messageContent = newMessage.trim();
    setNewMessage('');

    // Try WebSocket first
    if (isConnected && wsSendMessage(partner.id, messageContent)) {
      // Message sent via WebSocket - it will appear via the subscription
      setSending(false);
      inputRef.current?.focus();
      return;
    }

    // Fallback to HTTP if WebSocket not available
    try {
      await api.post('/messages', {
        recipient_id: partner.id,
        content: messageContent
      });
      fetchMessages();
      inputRef.current?.focus();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
      setNewMessage(messageContent); // Restore message on error
    } finally {
      setSending(false);
    }
  };

  const onEmojiClick = (emojiData) => {
    setNewMessage(prev => prev + emojiData.emoji);
    inputRef.current?.focus();
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return `Aujourd'hui à ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (!isOpen || !partner) return null;

  // Minimized state - just show header bar
  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-0 z-50 bg-dark border border-white/10 rounded-t-lg shadow-xl cursor-pointer"
        style={{ left: `${288 + (position * 250)}px`, width: '240px' }}
        onClick={onMinimize}
      >
        <div className="p-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to={`/member/${partner.id}`} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Avatar className="w-6 h-6">
                {profile?.profile_picture ? (
                  <AvatarImage src={getImageUrl(profile.profile_picture)} />
                ) : (
                  <AvatarFallback className="bg-dark-subtle text-gold text-xs">
                    {partner.pseudo?.[0]?.toUpperCase()}
                  </AvatarFallback>
                )}
              </Avatar>
              <span className="text-white text-sm font-medium truncate max-w-[120px] hover:text-gold transition-colors">{partner.pseudo}</span>
            </Link>
            <OnlineIndicator isOnline={profile?.is_online} size="sm" />
          </div>
          <div className="flex items-center gap-1">
            <Maximize2 className="w-4 h-4 text-gray-400" />
            <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="p-1 hover:bg-white/10 rounded">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed bottom-0 z-50 bg-dark-paper border border-white/10 rounded-t-lg shadow-2xl flex flex-col"
      style={{ 
        left: `${288 + (position * 330)}px`, 
        width: '320px',
        height: '420px'
      }}
      data-testid={`chat-popup-${partner.id}`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-dark to-dark-subtle border-b border-white/10 rounded-t-lg">
        {/* Top bar with name and controls */}
        <div className="px-3 py-2 flex items-center justify-between">
          <Link to={`/member/${partner.id}`} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Avatar className="w-8 h-8 border border-gold/30">
              {profile?.profile_picture ? (
                <AvatarImage src={getImageUrl(profile.profile_picture)} />
              ) : (
                <AvatarFallback className="bg-dark-subtle text-gold text-sm">
                  {partner.pseudo?.[0]?.toUpperCase()}
                </AvatarFallback>
              )}
            </Avatar>
            <div>
              <div className="flex items-center gap-1.5">
                <span className="text-white font-semibold text-sm">{partner.pseudo}</span>
                <OnlineIndicator isOnline={profile?.is_online} size="sm" />
              </div>
            </div>
          </Link>
          
          <div className="flex items-center gap-1">
            {/* WebSocket connection indicator */}
            <div className="px-1" title={isConnected ? "Chat en temps réel actif" : "Mode hors-ligne"}>
              {isConnected ? (
                <Wifi className="w-3.5 h-3.5 text-green-500" />
              ) : (
                <WifiOff className="w-3.5 h-3.5 text-gray-500" />
              )}
            </div>
            <button 
              onClick={onClose} 
              className="p-1.5 hover:bg-white/10 rounded transition-colors"
              title="Fermer"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
        
        {/* Profile info bar */}
        <div className="px-3 pb-2 flex items-center gap-2 text-xs text-gray-400">
          {profile?.profile_type && (
            <span className="capitalize">{profile.profile_type}</span>
          )}
          {profile?.age && (
            <span>• {profile.age} ans</span>
          )}
          {profile?.location && (
            <span>• {profile.location}</span>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 scrollbar-visible bg-dark/30">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-5 h-5 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            <p>Aucun message</p>
            <p className="text-xs mt-1">Commencez la conversation !</p>
          </div>
        ) : (
          <>
            {messages.map((msg, index) => {
              const isOwn = msg._isOwn === true || msg.sender_id === user.id || msg.sender_pseudo === user.pseudo;
              const showDate = index === 0 || 
                new Date(msg.created_at).toDateString() !== new Date(messages[index - 1].created_at).toDateString();
              
              return (
                <React.Fragment key={msg.id}>
                  {showDate && (
                    <div className="flex justify-center my-2">
                      <span className="text-xs text-gray-500 bg-dark/50 px-2 py-1 rounded">
                        {formatTime(msg.created_at)}
                      </span>
                    </div>
                  )}
                  <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                    <div 
                      className={`max-w-[80%] px-3 py-2 rounded-2xl ${
                        isOwn 
                          ? 'bg-gold text-dark rounded-br-sm' 
                          : 'bg-dark-subtle text-white rounded-bl-sm'
                      }`}
                    >
                      <p className="text-sm break-words">{msg.content}</p>
                      <span className={`text-[10px] ${isOwn ? 'text-dark/60' : 'text-gray-500'} mt-1 block text-right`}>
                        {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="border-t border-white/10 p-2 bg-dark">
        <form onSubmit={sendMessage} className="flex items-center gap-2">
          {/* Emoji picker */}
          <div className="relative" ref={emojiRef}>
            <button
              type="button"
              onClick={() => setShowEmoji(!showEmoji)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <Smile className="w-5 h-5 text-gray-400 hover:text-gold" />
            </button>
            
            {showEmoji && (
              <div className="absolute bottom-12 left-0 z-50">
                <EmojiPicker
                  onEmojiClick={onEmojiClick}
                  theme="dark"
                  width={280}
                  height={350}
                  searchPlaceholder="Rechercher..."
                  previewConfig={{ showPreview: false }}
                />
              </div>
            )}
          </div>

          {/* Input */}
          <Input
            ref={inputRef}
            type="text"
            placeholder="Votre message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            className="flex-1 bg-dark-subtle border-white/10 text-white text-sm h-9 placeholder:text-gray-500"
            disabled={sending}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage(e)}
          />

          {/* Send button */}
          <button
            type="submit"
            disabled={sending || !newMessage.trim()}
            className="p-2 bg-gold hover:bg-gold-light rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4 text-dark" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatPopup;
